import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import client from "../../lib/api";
import { fireConfetti } from "../../lib/confetti";
import iconMark from "../../assets/brand/klareet-icon-white.png";

// Klair narrates, in first person, what she is piecing together.
const LINES = [
  "reading back through everything you told me",
  "listening for the shape underneath your words",
  "lining up the layers that kept showing up",
  "writing your honest line, in plain language",
  "settling on the archetype that fits you best",
];

/**
 * A10: Building your read. Klair narrates the handover while the
 * report finishes rendering client-side. The report is already
 * synthesised by the time we reach here; this screen is the soft,
 * alive moment before the reveal.
 */
export default function BuildingReadScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [line, setLine] = useState(0);
  const [name, setName] = useState("");
  const [celebrate, setCelebrate] = useState(false);
  const celebRef = useRef(null);

  useEffect(() => {
    let alive = true;
    client
      .get("/consent/status")
      .then((r) => {
        if (!alive) return;
        const full = (r.data?.display_name || "").trim();
        if (full) setName(full.split(/\s+/)[0]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setLine((i) => Math.min(LINES.length - 1, i + 1)), 850);
    // Once the lines land, play a short celebration beat, then send the
    // person straight to their Snapshot.
    const celebrateAt = setTimeout(() => {
      setCelebrate(true);
      if (celebRef.current) fireConfetti(celebRef.current, { hue: "#0f766e" });
    }, 4400);
    const done = setTimeout(() => {
      nav(`${base}/report`, { replace: true });
    }, 6200);
    return () => { clearInterval(iv); clearTimeout(celebrateAt); clearTimeout(done); };
  }, [nav, base]);

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-blue"  style={{ top: "-10rem", left: "-6rem", width: "24rem", height: "24rem", opacity: 0.6 }} />
      <div className="k-orb k-orb-teal"  style={{ top: "6rem",   right: "-8rem", width: "22rem", height: "22rem", opacity: 0.6 }} />
      <div className="k-orb k-orb-amber" style={{ bottom: "-8rem", left: "18%", width: "20rem", height: "20rem", opacity: 0.55 }} />

      {celebrate && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center k-fade-in"
          style={{ background: "hsl(var(--klareet-canvas) / 0.82)", backdropFilter: "blur(4px)" }}
          data-testid="building-celebration"
        >
          <div ref={celebRef} className="text-center px-6">
            <div
              className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center k-celebrate-pop"
              style={{ background: "hsl(var(--klareet-teal))", color: "white" }}
            >
              <Sparkles size={34} />
            </div>
            <div className="k-chip k-chip-teal mb-3 mx-auto">it's ready</div>
            <h1 className="klareet-serif" style={{ fontSize: "clamp(1.6rem, 4vw, 2.3rem)", lineHeight: 1.1, color: "hsl(var(--klareet-ink))" }} data-testid="building-celebration-title">
              Your read is ready{name ? `, ${name}` : ""}.
            </h1>
            <p className="k-quiet mt-2">Taking you to your snapshot.</p>
          </div>
        </div>
      )}

      <div className="relative max-w-2xl mx-auto px-5 pt-24 pb-24 text-center">
        <div className="relative mx-auto mb-8 w-28 h-28 rounded-[28px] flex items-center justify-center"
             style={{ background: "linear-gradient(135deg, #1A1D3A, #2B2150 60%, #3C1B4E)" }}>
          <span className="k-ring-pulse" aria-hidden="true"></span>
          <span className="k-ring-pulse" aria-hidden="true" style={{ animationDelay: "0.7s" }}></span>
          <img src={iconMark} alt="" aria-hidden="true" className="w-16 h-16" data-testid="building-icon" />
        </div>

        <div className="k-chip k-chip-teal mb-3 mx-auto">building your read</div>
        <h1 className="klareet-serif mb-6" style={{ fontSize: "clamp(1.5rem, 3.6vw, 2.1rem)", lineHeight: 1.15 }} data-testid="building-title">
          Give me a few seconds{name ? `, ${name}` : ""}. I'm putting your read together.
        </h1>

        <ol className="mt-6 space-y-2 max-w-md mx-auto text-left" data-testid="building-lines">
          {LINES.map((t, i) => (
            <li
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl transition-all"
              style={{
                background: i <= line ? "hsl(var(--klareet-blue-soft))" : "hsl(var(--klareet-canvas-2))",
                border: "1px solid hsl(var(--klareet-line-soft))",
                opacity: i > line ? 0.5 : 1,
              }}
              data-testid={`build-line-${i}${i <= line ? "-active" : ""}`}
            >
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center klareet-mono shrink-0"
                style={{
                  background: i < line ? "hsl(var(--klareet-blue))" : i === line ? "hsl(var(--klareet-blue))" : "hsl(var(--klareet-line))",
                  color: i <= line ? "white" : "hsl(var(--klareet-muted))",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {i < line ? "\u2713" : i + 1}
              </span>
              <span style={{ color: "hsl(var(--klareet-ink))" }}>{t}</span>
              {i === line && line < LINES.length - 1 && (
                <span className="k-build-dots ml-auto" aria-hidden="true">
                  <span></span><span></span><span></span>
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
